import { type Client, type RouteGenerator } from "silver";
import { type BookLaunchData, type BookLaunchDataService } from "shared/Services/BookLaunchDataService";

export type BookURLOptions = {
	projectId?: number;
	includeCallingURL?: boolean;
	readaloudSessionId?: number;
};

export type BookReadOptions = BookURLOptions & {
	inNewTab?: boolean;
	sourceX?: number;
	sourceY?: number;
	source?: HTMLElement;
	callingBand?: number;
};

export class ReadBookService {

	private effectGuards: Array<() => boolean> = [];
	public forceNewTab: boolean = false;

	constructor(
		private client: Client,
		private router: RouteGenerator,
		private bookLaunchDataService: BookLaunchDataService
	) { }

	public readBook(archiveCode: string, options: BookReadOptions = { }): void {
		if (this.forceNewTab) {
			options.inNewTab = true;
		}
		const bookLaunchData: BookLaunchData = { };
		const url = this.getBookUrl(archiveCode, { ...options, includeCallingURL: true });
		if (options.callingBand) {
			bookLaunchData.callingBand = options.callingBand;
		}

		this.bookLaunchDataService.setBookLaunchDataForBook(archiveCode, bookLaunchData);

		if ("source" in options) {
			const rect = (options.source as HTMLElement).getBoundingClientRect();
			options.sourceX = rect.left + rect.width / 2;
			options.sourceY = rect.top + rect.height / 2;
		}
		this.client.open(url, { target: (options.inNewTab ? "_blank" : "_self") });
		this.triggerEffect(options);
	}

	public readBookFromEvent(archiveCode: string, event: Event|undefined, options: BookReadOptions = { }): void {
		if (event && event instanceof MouseEvent) {
			options.inNewTab = (event.metaKey || event.ctrlKey || event.shiftKey);
			options.sourceX = event.clientX;
			options.sourceY = event.clientY;
		} else if (event && (event.target instanceof HTMLElement)) {
			options.source = event.target;
		}
		this.readBook(archiveCode, options);
	}

	public getBookUrl(archiveCode: string, options: BookURLOptions = { }): string {
		const params: any = { };
		params.archive_code = archiveCode;
		if (options.projectId) {
			params.projectId = options.projectId;
		}
		if (options.includeCallingURL) {
			params.calling_url = this.client.url.path;
		}
		if (options.readaloudSessionId) {
			params.s = String(options.readaloudSessionId);
		}
		return this.router.generate("reader/index", "index", params);
	}

	/**
	 * Add a callback to check if effects can be triggered. Useful to prevent animations
	 * when beforeunload events are triggered
	 */
	public guardEffects(canTriggerEffects: () => boolean): void {
		this.effectGuards.push(canTriggerEffects);
	}

	private triggerEffect(options: BookReadOptions = { }): HTMLDivElement|null {
		if (!(options.sourceX && options.sourceY) || options.inNewTab) {
			return null;
		}
		for (const canTriggerEffects of this.effectGuards) {
			if (!canTriggerEffects()) {
				return null;
			}
		}
		const effect = document.createElement("div");
		effect.style.left = `${options.sourceX}px`;
		effect.style.top = `${options.sourceY}px`;
		effect.classList.add("material-open", "reader");
		document.body.appendChild(effect);
		setTimeout(() => {
			effect.classList.add("animate");
		}, 50);
		return effect;
	}
}
